import queryString from "query-string";
import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { schoolSignUpFormResolver } from "../../../../constants/resolvers";
import { invitationCodeSchema, emailSchema } from "../../../../constants/schemas";
import {
  useLoginSchoolMutation,
  useSignupSchoolMutation,
  useVerifyEmailMutation,
  useValidateInvitationCodeMutation,
  useVerifyPhoneNumberMutation
} from "../../../../store/auth/auth.api";
import { useSendOTPWithRecaptcha, useVerifyOTPMutation } from "../../../../store/auth/otp.api";
import { phoneNumberParserUtils } from "../../../../utils/phoneNumber-parser-utils";
import { useTimer } from "react-timer-hook";
import OtpInput from "react-otp-input";
import { PhoneInput } from "../../../components/form/PhoneInput";
import { useRecaptchaDismissalDetector } from "../../../../hooks/useRecaptchaDismissalDetector";
import { schoolCreateAccFormFields } from "../../../../constants/constants";
import { DEFAULT_COUNTRY } from "../../../../constants/country-config";
import loaderGif from "../../../../assets/gifs/LoaderGif.gif";

let { firstName, emailId, inviteCode, schoolName, phone, TC } = schoolCreateAccFormFields;

const defaultValues = ({ CODE }) => ({
  otpVerified: false,
  invitationCode: CODE || "",
  formLoginFlag: false,
  email: "",
  udise_code: ""
});

export const SchoolSignUpForm = ({ toggleTAndC, togglePAndP }) => {
  const { CODE = null } = queryString.parse(useLocation().search);
  const [onRegister] = useSignupSchoolMutation();
  const [onLoginSchool] = useLoginSchoolMutation();

  const [validateInvitationCode] = useValidateInvitationCodeMutation();
  const [verifyEmail, { isSuccess: verifyEmailIsSuccess }] = useVerifyEmailMutation();
  const [verifyOTP, { isSuccess: verifyOTPIsSuccess }] = useVerifyOTPMutation();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isTimerCompleted, setIsTimerCompleted] = useState(false);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [otpValue, setOtpValue] = useState(Array(4).fill(""));
  const [loader, setLoader] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);

  const [sendOTP, { isLoading: sendOTPLoading, isUninitialized }] = useSendOTPWithRecaptcha();
  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {
      setTimerExpired(true);
      setIsTimerCompleted(true);
    }
  });
  const [verifyPhoneNumber] = useVerifyPhoneNumberMutation();

  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setLoader(false);
      setIsButtonDisabled(false);
      setError("phoneNumber", {
        type: "recaptcha",
        message: "Security verification cancelled. Please try again."
      });
    }
  });

  const form = useForm({
    defaultValues: defaultValues({ CODE }),
    resolver: schoolSignUpFormResolver,
    context: {
      phoneLength: selectedCountry.phoneLength
    },
    mode: "onChange"
  });
  const { formState } = form;
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    setError,
    clearErrors,
    setValue
  } = form;
  const { isSubmitting } = formState;
  const [formLoginFlag] = watch(["formLoginFlag"]);
  const defaultCheck = watch([
    "firstName",
    "lastName",
    "email",
    "schoolName",
    "invitationCode",
    "otpCode",
    "phoneNumber",
    "udise_code"
  ]);
  const [phoneNumber, ...otpCodes] = watch(["phoneNumber", ...Array(4).fill("otpCode")]);

  // Dynamic phone number validation based on selected country
  const phoneNumberIsValid = useMemo(
    () => phoneNumber?.length === selectedCountry.phoneLength,
    [phoneNumber, selectedCountry]
  );

  const handleInputPaste = (event) => {
    const pastedText = event.clipboardData.getData("text/plain");
    const sanitizedText = pastedText.replace(/\s/g, "");
    event.preventDefault();
    document.execCommand("insertText", false, sanitizedText);
  };

  useEffect(() => {
    if (timerExpired && isOtpSent) {
      setTimerExpired(false);
    }
    if (timerExpired && !isOtpSent) {
      setShowOtpInput(false);
      setTimerExpired(false);
    }

    return cleanup;
  }, [timerExpired, isOtpSent, cleanup]);

  useEffect(() => {
    if (!formLoginFlag && !isOtpSent) {
      const isAllFieldsFilled = firstName && schoolName && phone && TC && inviteCode && emailId;
      const udiseValue = watch("udise_code") || "";
      const hasUdiseError = errors.udise_code && errors.udise_code.message && udiseValue.length > 0;
      const isPhoneValid = phoneNumber?.length === selectedCountry.phoneLength;
      const shouldDisable = !isAllFieldsFilled || hasUdiseError || !isPhoneValid;
      setIsButtonDisabled(shouldDisable);
    } else if (isOtpSent) {
      const isAllFieldsFilled = firstName && schoolName && phone && TC && inviteCode && emailId;
      const udiseValue = watch("udise_code") || "";
      const hasUdiseError = errors.udise_code && errors.udise_code.message && udiseValue.length > 0;
      const isPhoneValid = phoneNumber?.length === selectedCountry.phoneLength;
      const shouldDisable = !isAllFieldsFilled || hasUdiseError || !isPhoneValid;
      const isOtpValid = otpValue.join("").length === 4;
      setIsButtonDisabled(shouldDisable || !isOtpValid);
    }
  }, [
    otpValue,
    isOtpSent,
    errors.udise_code,
    firstName,
    schoolName,
    phone,
    TC,
    inviteCode,
    emailId,
    phoneNumber,
    selectedCountry,
    formLoginFlag,
    watch
  ]);

  const checkValidCode = async (code) => {
    if (invitationCodeSchema.isValidSync(code)) {
      const result = await validateInvitationCode(code);
      form.clearErrors("invitationCode");
      if (!result?.data?.valid_code) {
        setError("invitationCode", { message: "Code is not valid" });
        setIsButtonDisabled(true && !formLoginFlag);
        inviteCode = false;
      } else if (!result?.data?.status) {
        setError("invitationCode", { message: "Invitation code is used or invalid! Please use a different code." });
        inviteCode = false;
        setIsButtonDisabled(true && !formLoginFlag);
      } else {
        clearErrors("invitationCode");
        inviteCode = true;
      }
    }
  };

  const checkEmail = async (email) => {
    if (emailSchema.isValidSync(email)) {
      const result = await verifyEmail(email);
      if (result.data.exist) {
        emailId = false;
        setError("email", { message: "Email is already registered with us" });
      } else {
        emailId = true;
        clearErrors("email");
      }
    } else {
      emailId = false;
    }
  };

  const onSubmit = async (data) => {
    const phoneNumber = data.phoneNumber;
    if (formLoginFlag) {
      const body = {
        isd_code: selectedCountry.isdCode,
        phone: data.phoneNumber,
        password: `school${phoneNumber.slice(-3)}`
      };
      const loginRes = await onLoginSchool(body);
      console.log("Login Result:", loginRes);
    } else {
      if (!isButtonDisabled) {
        if (verifyOTPIsSuccess) {
          let payload = {
            first_name: data.firstName,
            ...(data.lastName ? { last_name: data.lastName } : {}),
            primary_contact_info: {
              name: data.firstName,
              isd_code: selectedCountry.isdCode,
              phone: data.phoneNumber
            },
            school_name: data.schoolName,
            email: data.email,
            whatsapp_consent: data.whatsappConsent,
            activation_code: data.invitationCode,
            udise_code: data.udise_code || ""
          };

          const registerResult = await onRegister(payload);
        }
      }
    }
  };

  const clickSendOTP = async () => {
    setOtpValue(Array(4).fill(""));
    setError("otpCode", { type: "custom", message: "" });
    setIsButtonDisabled(true);
    setLoader(true);

    if (!phoneNumber || phoneNumber.length !== selectedCountry.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${selectedCountry.phoneLength} digits`
      });
      setIsButtonDisabled(false);
      setLoader(false);
      return false;
    }

    setIsTimerCompleted(false);

    setupDismissalListener();
    try {
      const otpResult = await sendOTP({
        phoneNumber: phoneNumber,
        isdCode: selectedCountry.isdCode
      });

      if (otpResult?.data) {
        const time = new Date();
        time.setSeconds(time.getSeconds() + 30);
        restart(time);
        setIsEditEnabled(true);
        setLoader(false);
        return true;
      } else {
        const errorData = otpResult?.error?.data || otpResult?.error || otpResult;
        const errorMessage = errorData?.message || errorData?.detail || "Failed to send OTP";
        const errorDetail = errorData?.detail || "";

        if (errorDetail.includes("reCAPTCHA")) {
          setError("phoneNumber", {
            type: "recaptcha",
            message: "Security verification failed. Please try again."
          });
        } else {
          setError("phoneNumber", { type: "api", message: errorMessage });
        }
        setIsEditEnabled(false);
        setIsButtonDisabled(false);
        setLoader(false);
        return false;
      }
    } catch (error) {
      setLoader(false);
      setIsButtonDisabled(false);

      const errorData = error?.error?.data || error?.error || error;
      const errorMessage = errorData?.message || errorData?.detail || "An unexpected error occurred. Please try again.";

      setError("phoneNumber", { type: "api", message: errorMessage });
      setIsEditEnabled(false);
      return false;
    }
  };

  const handleSignUp = async () => {
    const otpString = otpValue.join("");
    setIsTimerCompleted(true);
    await trigger("otpCode");

    if (otpString.length === 4) {
      setValue("otpCode", otpString);
      const result = await verifyOTP({
        otpCode: otpString,
        phoneNumber: phoneNumber,
        isdCode: selectedCountry.isdCode
      });

      if (result?.data?.detail?.includes("user does not exist")) {
        setError("phoneNumber", { type: "custom", message: "Mobile number is not registered. Please register." });
        setLoader(false);
      } else if (!result?.data) {
        setError("otpCode", { type: "custom", message: "OTP is incorrect or not valid" });
        setIsButtonDisabled(true);
        setLoader(false);
      } else if (result?.data) {
        const formData = form.getValues();

        let payload = {
          first_name: formData.firstName,
          ...(formData.lastName ? { last_name: formData.lastName } : {}),
          primary_contact_info: {
            name: formData.firstName,
            isd_code: selectedCountry.isdCode,
            phone: formData.phoneNumber
          },
          school_name: formData.schoolName,
          email: formData.email,
          whatsapp_consent: formData.whatsappConsent,
          activation_code: formData.invitationCode,
          udise_code: formData.udise_code || ""
        };
        const res = await onRegister(payload);

        // Handle UDISE code already exists error
        if (res?.data?.detail === "UDISE code already exists" || res?.data?.udise_code_exist) {
          setError("udise_code", {
            type: "custom",
            message: "UDISE Code already exists"
          });
          setIsButtonDisabled(true);
        } else if (res.data?.detail) {
          setError("email", { type: "custom", message: "Email is already registered with us" });
        } else if (res.data?.message) {
          setError("invitationCode", { type: "custom", message: res.data.message });
        } else if (res.error) {
          setError("phoneNumber", { type: "custom", message: "Registration failed. Please try again." });
        }
        setLoader(false);
      } else if (result?.error?.data?.error) {
        setError("otpCode", { type: "custom", message: "OTP is incorrect or not valid" });
        setIsButtonDisabled(true);
        setLoader(false);
      } else {
        setIsButtonDisabled(false);
        setLoader(false);
      }
    } else {
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
      setLoader(false);
    }
  };

  const handleButtonAction = async () => {
    if (formLoginFlag) {
      await handleSubmit(onSubmit)();
    } else {
      if (!isOtpSent) {
        const success = await clickSendOTP();
        if (success) {
          setIsDisable(true);
          setIsOtpSent(true);
          setShowOtpInput(true);
          setIsButtonDisabled(true);
        } else {
          setIsButtonDisabled(false);
        }
      } else {
        setLoader(true);
        await handleSignUp();
        setLoader(false);
      }
    }
  };

  const handleEditNumberClick = () => {
    setIsDisable(false);
    phone = false;
    setIsButtonDisabled(true);
    setIsEditEnabled(false);
    setShowOtpInput(false);
    setIsOtpSent(false);
    setValue("phoneNumber", "");
    setOtpValue(Array(4).fill(""));
    clearErrors("phoneNumber");
    Array(4)
      .fill("")
      .forEach((_, index) => {
        clearErrors(`otpCode[${index}]`);
      });
    restart();
    setIsTimerCompleted(true);
  };

  const handlePhoneNumberChange = async (phoneNumber, country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", phoneNumber);

    if (loader) return;

    if (phoneNumber.length < country.phoneLength && phoneNumber.length > 0) {
      clearErrors("phoneNumber");
      const isPhoneValid = phoneNumber.length === country.phoneLength;
      const isAllFieldsFilled = firstName && schoolName && phone && TC && inviteCode && emailId;
      const udiseValue = watch("udise_code") || "";
      const hasUdiseError = errors.udise_code && errors.udise_code.message && udiseValue.length > 0;
      const shouldDisable = !isAllFieldsFilled || hasUdiseError || !isPhoneValid;
      setIsButtonDisabled(shouldDisable);
      return;
    }

    if (phoneNumber.length > country.phoneLength) {
      setError("phoneNumber", {
        message: `Phone number must be ${country.phoneLength} digits`
      });
      setIsButtonDisabled(true);
      return;
    }

    if (phoneNumber.length === country.phoneLength) {
      const result = await verifyPhoneNumber(phoneNumber);
      if (result?.data?.phone_exist && result?.data?.is_active) {
        phone = false;
        setValue("formLoginFlag", false);
        setIsOtpSent(false);
        setShowOtpInput(false);
        setIsButtonDisabled(true);
        setError("phoneNumber", {
          message: "School Already Registered and Onboarded"
        });
      } else if (result?.data?.phone_exist && !result?.data?.is_active) {
        setError("phoneNumber", {
          message: "Phone Number is already registered, click on Continue to finish onboarding"
        });
        setValue("formLoginFlag", true);
        phone = true;
        setIsOtpSent(false);
        setShowOtpInput(false);
        setIsButtonDisabled(false);
      } else {
        phone = true;
        clearErrors("phoneNumber");

        const isAllFieldsFilled = firstName && schoolName && phone && TC && inviteCode && emailId;
        const udiseValue = watch("udise_code") || "";
        const hasUdiseError = errors.udise_code && errors.udise_code.message && udiseValue.length > 0;
        const shouldDisable = !isAllFieldsFilled || hasUdiseError;
        setIsButtonDisabled(shouldDisable);
      }
    }
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setValue("phoneNumber", "");
    clearErrors("phoneNumber");
    setIsButtonDisabled(true);

    if (isOtpSent) {
      setIsOtpSent(false);
      setShowOtpInput(false);
      setOtpValue(Array(4).fill(""));
      clearErrors("otpCode");
      setIsEditEnabled(false);
      restart();
      setIsTimerCompleted(true);
    }
  };

  const onBlur = async (fieldName) => {
    if (fieldName === "phoneNumber") {
      if (phoneNumber && phoneNumber.length > 0) {
        if (phoneNumber.length !== selectedCountry.phoneLength) {
          setError("phoneNumber", {
            message: `Phone number must be ${selectedCountry.phoneLength} digits`
          });
        } else {
          clearErrors("phoneNumber");
        }
      } else if (!phoneNumber || phoneNumber.length === 0) {
        setError("phoneNumber", {
          message: `Phone number is required`
        });
      }
    } else {
      await trigger(fieldName);
    }
  };

  const handleInvitaionCodeChange = (e) => {
    const inputValue = e.target.value;
    invitationCodeSchema
      .validate(inputValue)
      .then(() => {
        inviteCode = true;
      })
      .catch((error) => {
        setError("invitationCode", { message: error.message });
        setIsButtonDisabled(true && !formLoginFlag);
      });
  };

  const handleOtpCodeChange = (e) => {
    const enteredOtp = e.trim();

    if (enteredOtp.length === 4) {
      const otpFormatIsValid = /^[0-9]{4}$/.test(enteredOtp);
      if (otpFormatIsValid) {
        setOtpValue([...enteredOtp]);
        clearErrors("otpCode");
      } else {
        setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
        setIsButtonDisabled(true);
      }
    } else {
      setOtpValue([...enteredOtp]);
      setError("otpCode", { type: "custom", message: "OTP must be exactly 4 digits." });
      setIsButtonDisabled(true);
    }
  };

  const disabled = useMemo(() => {
    return {
      phoneForm: isRunning || sendOTPLoading || isSubmitting,
      otpForm: !(isRunning || !isUninitialized) || isSubmitting,
      verifyButton: false
    };
  }, [isRunning, isUninitialized, sendOTPLoading, isSubmitting, phoneNumberIsValid]);

  const handleInputKeyPress = async (field) => {
    switch (field.name) {
      case "firstName":
        const nameValue = field.value.replace(/^\s+/g, "").replace(/[^A-Za-z\s]/gi, "");
        if (nameValue.length === 0) {
          setValue(field.name, "");
          firstName = false;
        } else if (/^[A-Za-z][A-Za-z\s]*$/.test(nameValue)) {
          setValue(field.name, nameValue);
          firstName = true;
        } else {
          setValue(field.name, field.value.trimLeft());
          firstName = false;
        }
        break;
      case "lastName":
        const lastNameValue = field.value.replace(/[^A-Za-z\s]+/gi, "");
        setValue(field.name, lastNameValue);
        break;
      case "email":
        const mailValue = field.value.replace(/\s/g, "");
        setValue("email", mailValue);
        checkEmail(mailValue);
        break;
      case "schoolName":
        const schoolValue = field.value.replace(/[^A-Za-z\s]/gi, "");
        setValue(field.name, schoolValue);
        schoolName = field.value.length > 1;
        break;
      case "udise_code":
        const udiseValue = field.value.replace(/\D/g, "");
        setValue(field.name, udiseValue);

        if (udiseValue && udiseValue.length > 0) {
          if (udiseValue.length === 11) {
            clearErrors("udise_code");
          } else {
            setError("udise_code", {
              type: "custom",
              message: "UDISE Code must be 11 digits"
            });
          }
        } else {
          clearErrors("udise_code");
        }
        break;
      case "invitationCode":
        checkValidCode(field.value);
        setValue(field.name, field.value);
        break;
      case "whatsappConsent":
        TC = field?.checked;
        break;
      case "phoneNumber":
        break;
      default:
        break;
    }
  };

  return (
    <form onChange={(e) => handleInputKeyPress(e.target)} onSubmit={handleSubmit(onSubmit)}>
      <div className="membership__card membership__card_mob mx-auto ms-md-auto">
        <div className="tab-content">
          <h3 className="tab-heading">Apply for Membership </h3>
          <div className="row">
            <div className="col-6 pe-2 mb-12">
              <label className="tab-lable">First name</label>
              <input
                type="text"
                required
                className={`member--inputbox ${errors.firstName ? "error--strike" : "input-highlight"}`}
                {...register("firstName")}
                disabled={isDisable}
              />
              {errors.firstName && <span className="error--strike--text">{errors.firstName.message}</span>}
            </div>
            <div className="col-6 ps-2 mb-12">
              <label className="tab-lable">Last name</label>
              <input
                type="text"
                className={`member--inputbox ${errors.lastName ? "error--strike" : ""}`}
                {...register("lastName")}
                disabled={isDisable}
              />
              {errors.lastName && <span className="error--strike--text">{errors.lastName.message}</span>}
            </div>
            <div className="col-12 mb-12">
              <label className="tab-lable">Email ID</label>
              <input
                className={`member--inputbox ${
                  errors.email ? "error--strike" : verifyEmailIsSuccess ? "input-highlight green" : ""
                }`}
                placeholder="Enter your email id"
                {...register("email")}
                disabled={isDisable}
              />
              {errors.email && <span className="error--strike--text">{errors.email.message}</span>}
            </div>
            <div className="col-12 mb-12">
              <label className="tab-lable">School Name</label>
              <input
                type="text"
                required
                className={`member--inputbox ${errors.schoolName ? "error--strike" : "input-highlight"}`}
                placeholder="Enter name"
                {...register("schoolName")}
                disabled={isDisable}
              />
              {errors.schoolName && <span className="error--strike--text">{errors.schoolName.message}</span>}
            </div>

            <div className="col-12 mb-12">
              <label className="tab-lable">UDISE Code (Optional)</label>
              <input
                className={`member--inputbox ${
                  errors.udise_code ? "error--strike" : watch("udise_code")?.length === 11 ? "input-highlight" : ""
                }`}
                placeholder="Enter 11-digit UDISE code"
                {...register("udise_code")}
                onInput={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                  e.target.value = value;
                  setTimeout(() => handleInputKeyPress(e.target), 0);
                }}
                disabled={isDisable}
              />
              {errors.udise_code && <span className="error--strike--text">{errors.udise_code.message}</span>}
            </div>

            <div className="col-12 mb-12">
              <label className="tab-lable">Invitation Code</label>
              <input
                type="text"
                required
                className={`member--inputbox ${errors.invitationCode ? "error--strike" : "input-highlight"}`}
                placeholder="Enter invitation code"
                {...register("invitationCode")}
                onPaste={handleInputPaste}
                onChange={handleInvitaionCodeChange}
                disabled={isDisable}
              />
              {errors.invitationCode && <span className="error--strike--text">{errors.invitationCode.message}</span>}
            </div>

            <div className="position-relative">
              <label className="tab-lable">Phone Number</label>
              <PhoneInput
                form={form}
                disabled={isEditEnabled || loader}
                handleChange={handlePhoneNumberChange}
                onBlur={() => onBlur("phoneNumber")}
                handleEditNumberClick={handleEditNumberClick}
                isEditEnabled={isEditEnabled}
                onCountryChange={handleCountryChange}
              />
              {errors.phoneNumber && <span className="error--strike--text">{errors.phoneNumber.message}</span>}
              {disabled.verifyButton &&
                otpCodes.map((_, index) => {
                  return (
                    errors[`otpCode[${index}]`] && (
                      <span className="error--strike--text" key={index}>
                        {errors[`otpCode[${index}]`].message}
                      </span>
                    )
                  );
                })}
            </div>
            {showOtpInput && (
              <div>
                <label className="tab-lable">Enter OTP</label>
                <div className="col-12 position-relative mb-12">
                  <div className="d-flex align-items-center">
                    <OtpInput
                      inputType="number"
                      skipDefaultStyles={false}
                      shouldAutoFocus="false"
                      value={otpValue.join("")}
                      onChange={(e) => handleOtpCodeChange(e)}
                      numInputs={4}
                      separator={<span> </span>}
                      isDisabled={disabled.otpForm}
                      inputStyle="otp-input-box"
                      renderInput={(props, index) => (
                        <input
                          {...props}
                          key={index}
                          className={`otp-input-box ${errors.otpCode?.message ? "otp-error-strick" : "highlight"}`}
                        />
                      )}
                    />
                    <div className="resend_otp">
                      {!isTimerCompleted && (
                        <span>
                          Resend OTP <br />({seconds} Sec.)
                        </span>
                      )}
                      {isTimerCompleted && (
                        <button className="resend-btn" onClick={clickSendOTP}>
                          Resend OTP
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {errors.otpCode && <span className="error--strike--text">{errors.otpCode.message}</span>}
              </div>
            )}
          </div>

          <div className="col-12 mb-3">
            <div className="form-check check-opacity">
              <label className="form-check-label text-muted">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="whatsappConsent"
                  {...register("whatsappConsent")}
                  defaultChecked={defaultCheck}
                />
                <i className="input-helper"></i> Notify me on WhatsApp <br />
              </label>
              <br />
              <span className="t-c_class">
                By Signing up, you agree with our{" "}
                <span onClick={toggleTAndC} className="gard--textflow mx-1">
                  T&C
                </span>{" "}
                and{" "}
                <span className="gard--textflow mx-1" onClick={togglePAndP}>
                  PP
                </span>
              </span>
              <br />
              {errors.whatsappConsent && <span className="error--strike--text">This field is required</span>}
            </div>
          </div>

          <button
            type="button"
            className="button--membersubmit w-100"
            onClick={handleButtonAction}
            disabled={isButtonDisabled}
          >
            {isOtpSent ? (
              loader ? (
                <div className="loader-container">
                  <img className="loader-gif" src={loaderGif} alt="Loading.." />
                  <p className="loader-text">Loading</p>
                </div>
              ) : (
                "Continue"
              )
            ) : loader ? (
              <div className="loader-container">
                <img className="loader-gif" src={loaderGif} alt="Loading.." />
                <p className="loader-text">Loading</p>
              </div>
            ) : (
              "Continue"
            )}
          </button>
        </div>
      </div>
    </form>
  );
};
